import { Control, FieldValues, FormState } from 'react-hook-form';
import { Paper, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TextInput from '../../forms/TextInput';
import SelectMenu from '../../forms/SelectMenu';
import { ApplicationTitle } from '../../../data/models/application-title.model';
import { UserCategoryType } from '../../../data/models/user-category-type.model';
import Checkbox from '../../forms/Checkbox';

const PREFIX = 'RoleForm';

export const classes = {
  container: `${PREFIX}-container`,
};

export const StyledBox = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

interface RoleFormProps {
  applications: ApplicationTitle[];
  categoryTypes: UserCategoryType[];
  control: Control<FieldValues, object>;
  formState: FormState<any>;
  requiredFieldMessage: string;
  isNewRole: boolean;
  readOnly: boolean;
}

export default function RoleForm({
  applications,
  categoryTypes,
  control,
  formState,
  requiredFieldMessage,
  isNewRole,
  readOnly,
}: RoleFormProps) {
  const { t } = useTranslation('common');

  return (
    <StyledBox className={classes.container}>
      <TextInput
        label={t('roles.roleName')}
        name="UserRoleName"
        required={true}
        disabled={readOnly || !isNewRole}
        control={control}
        errors={formState.errors}
        inputProps={{ maxLength: 30 }}
        rules={{
          required: requiredFieldMessage,
          pattern: {
            value: /^[a-zA-Z0-9 ]*$/,
            message: t('common.alphaNumericNoPeriod'),
          },
        }}
      />
      <TextInput
        label={t('roles.description')}
        name="Description"
        required={true}
        disabled={readOnly}
        control={control}
        errors={formState.errors}
        inputProps={{ maxLength: 80 }}
        rules={{
          required: requiredFieldMessage,
          pattern: {
            value: /^[a-zA-Z0-9., ]*$/,
            message: t('common.alphaNumeric'),
          },
        }}
      />
      <TextInput
        label={t('roles.displayOrder')}
        type="number"
        name="DisplayOrder"
        required={true}
        control={control}
        inputProps={{ maxLength: 3, min: 1, max: 999 }}
        rules={{
          required: requiredFieldMessage,
        }}
      />
      <SelectMenu
        label={t('roles.application')}
        name="ApplicationID"
        values={applications}
        valueAttr="applicationTitleID"
        labelAttr="applicationName"
        required={true}
        disabled={!isNewRole}
        control={control}
        rules={{ required: requiredFieldMessage }}
      />
      <SelectMenu
        label={t('roles.userCategory')}
        name="CategoryTypeID"
        values={categoryTypes}
        valueAttr="userCategoryTypeID"
        labelAttr="userCategoryTypeName"
        required={true}
        disabled={!isNewRole}
        control={control}
        rules={{ required: requiredFieldMessage }}
      />
      <Checkbox
        label={t('roles.isDefault')}
        name="IsDefault"
        control={control}
        disabled={readOnly}
      />
      <Checkbox
        label={t('common.active')}
        name="IsActive"
        control={control}
        disabled={readOnly}
      />
    </StyledBox>
  );
}
